import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { b2bAgenciesAPI, usersAPI, settingsAPI, prospectsAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { useCountry } from '../../context/CountryContext';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { isValidPhoneNumber, parsePhoneNumber } from 'libphonenumber-js';
import { Country, State, City } from 'country-state-city';
import { CITY_SELECT_OTHER_VALUE, getCityFieldState } from '../../utils/citySelect';
import { useTranslation } from 'react-i18next';

const LeadForm = () => {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const { getConfig } = useCountry();
  const config = getConfig();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [salesTeam, setSalesTeam] = useState([]);
  const [tiedUpAgencies, setTiedUpAgencies] = useState([]);
  const [courses, setCourses] = useState([]);
  const [courseCampusFees, setCourseCampusFees] = useState([]);
  const [intakes, setIntakes] = useState([]);
  const [sources, setSources] = useState([]);
  const [leadStatuses, setLeadStatuses] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [errors, setErrors] = useState({});
  const [phoneE164, setPhoneE164] = useState('');
  /** 'list' = city from dropdown; 'other' = custom name (not in country-state-city package). */
  const [cityEntryMode, setCityEntryMode] = useState('list');

  // B2B Agency module now supports only agency records.
  const initialType = 'B2B';

  const [form, setForm] = useState({
    lead_type: initialType, student_name: '', contact_number: '',
    phone_country_code: config.defaultPhoneCode, email: '', course_id: '', intake_id: '',
    source: initialType === 'B2B' ? 'Cold Call' : 'Website',
    decision_maker: '', funded_by: '', inquiry_by: initialType === 'B2B' ? 'Agency' : '',
    country: initialType === 'B2B' ? config.label : '',
    state: '',
    city: '',
    passport_no: '',
    gender: '',
    campus_id: '',
    location_country_code: initialType === 'B2B' ? config.flagCode : '',
    location_state_code: '',
    status: 'New', remarks: '', owner_id: '', agency_id: '',
    closed_by: ''
  });

  const countries = useMemo(() => Country.getAllCountries(), []);
  const states = useMemo(
    () => (form.location_country_code ? State.getStatesOfCountry(form.location_country_code) : []),
    [form.location_country_code]
  );
  const cities = useMemo(
    () => (form.location_country_code && form.location_state_code ? City.getCitiesOfState(form.location_country_code, form.location_state_code) : []),
    [form.location_country_code, form.location_state_code]
  );

  const sourceOptions = useMemo(() => {
    const activeSourceNames = (sources || []).map(s => s.source_name).filter(Boolean);
    if (form.source && !activeSourceNames.includes(form.source)) {
      // Ensure edit screens still show the current DB value even if it became inactive.
      return [form.source, ...activeSourceNames];
    }
    return activeSourceNames;
  }, [sources, form.source]);

  useEffect(() => {
    Promise.all([
      usersAPI.getSalesTeam(),
      settingsAPI.getCourses(),
      settingsAPI.getIntakes(),
      settingsAPI.getTiedUpAgencies(),
      settingsAPI.getSources(),
      settingsAPI.getLeadStatuses(),
      settingsAPI.getCampuses(),
      settingsAPI.getCourseCampusFees()
    ]).then(([usersRes, coursesRes, intakesRes, agenciesRes, sourcesRes, leadStatusesRes, campusesRes, courseCampusFeesRes]) => {
      setSalesTeam(usersRes.data);
      setCourses(coursesRes.data);
      setIntakes(intakesRes.data);
      setTiedUpAgencies(agenciesRes.data);
      setSources(sourcesRes.data || []);
      setLeadStatuses(leadStatusesRes.data || []);
      setCampuses((campusesRes.data || []).filter(c => c.status === 'active'));
      setCourseCampusFees((courseCampusFeesRes.data || []).filter(m => m.status === 'active'));
    });

    const fromProspectId = searchParams.get('fromProspect');
    if (!isEdit && fromProspectId) {
      prospectsAPI.getById(fromProspectId).then(res => {
        const p = res.data;
        const cc = p.phone_country_code || config.defaultPhoneCode;
        const nat = String(p.contact_number || '');
        const ccDigits = cc.replace(/^\+/, '');
        const cleanNat = nat.startsWith(ccDigits) ? nat.slice(ccDigits.length) : nat;
        setPhoneE164(cleanNat ? `${cc}${cleanNat}` : '');

        const pCountryCode = p.location_country_code || '';
        const pStateCode = p.location_state_code || '';
        if (pCountryCode && pStateCode) {
          const cityList = City.getCitiesOfState(pCountryCode, pStateCode);
          const { mode, cityForForm } = getCityFieldState(p.city || '', cityList);
          setCityEntryMode(mode);
          setForm(f => ({
            ...f,
            student_name: p.name || '',
            email: p.email || '',
            phone_country_code: cc,
            contact_number: p.contact_number || '',
            source: p.source || f.source,
            decision_maker: p.decision_maker || '',
            country: p.country || f.country,
            state: p.state || '',
            city: cityForForm,
            location_country_code: pCountryCode,
            location_state_code: pStateCode
          }));
        } else {
          setForm(f => ({
            ...f,
            student_name: p.name || '',
            email: p.email || '',
            phone_country_code: cc,
            contact_number: p.contact_number || '',
            source: p.source || f.source,
            decision_maker: p.decision_maker || '',
            country: p.country || f.country,
            state: p.state || '',
            city: p.city || ''
          }));
        }
      }).catch(() => {});
    }

    if (isEdit) {
      b2bAgenciesAPI.getById(id).then(res => {
        const lead = res.data;
        const isB2BLead = lead.lead_type === 'B2B';
        const normalizedCountryName = typeof lead.country === 'string' ? lead.country.trim().toLowerCase() : '';
        const normalizedStateName = typeof lead.state === 'string' ? lead.state.trim().toLowerCase() : '';
        const mappedCountry = lead.country
          ? countries.find(c => c.name.trim().toLowerCase() === normalizedCountryName) || null
          : null;
        const nextLocationCountryCode = mappedCountry?.isoCode || (isB2BLead ? 'IN' : '');
        const mappedStates = nextLocationCountryCode ? State.getStatesOfCountry(nextLocationCountryCode) : [];
        const mappedState = lead.state
          ? mappedStates.find(s => s.name.trim().toLowerCase() === normalizedStateName) || null
          : null;

        const cityList =
          nextLocationCountryCode && mappedState?.isoCode
            ? City.getCitiesOfState(nextLocationCountryCode, mappedState.isoCode)
            : [];
        const savedCityRaw = isB2BLead ? (lead.city || '') : '';
        const { mode: loadedCityMode, cityForForm } = getCityFieldState(savedCityRaw, cityList);

        const cc = lead.phone_country_code || config.defaultPhoneCode;
        const nat = String(lead.contact_number || '');
        const ccDigitsLoad = cc.replace(/^\+/, '');
        const cleanNat = nat.startsWith(ccDigitsLoad) ? nat.slice(ccDigitsLoad.length) : nat;
        const e164 = cleanNat ? `${cc}${cleanNat}` : '';
        setPhoneE164(e164);

        setForm({
          lead_type: lead.lead_type, student_name: lead.student_name,
          contact_number: lead.contact_number,
          phone_country_code: cc, email: lead.email,
          course_id: lead.course_id || '', intake_id: lead.intake_id || '',
          source: lead.source, decision_maker: lead.decision_maker || '',
          funded_by: lead.funded_by || '', inquiry_by: lead.inquiry_by || '',
          status: lead.status, remarks: lead.remarks || '', owner_id: lead.owner_id || '',
          agency_id: lead.agency_id || '',
          closed_by: lead.closed_by || '',

          // B2B location
          country: isB2BLead ? (lead.country || mappedCountry?.name || config.label) : (lead.country || ''),
          state: isB2BLead ? (lead.state || '') : '',
          city: isB2BLead ? cityForForm : (lead.city || ''),
          passport_no: lead.passport_no || '',
          gender: lead.gender || '',
          campus_id: lead.campus_id || '',
          location_country_code: isB2BLead ? nextLocationCountryCode : '',
          location_state_code: isB2BLead ? (mappedState?.isoCode || '') : ''
        });
        if (isB2BLead) setCityEntryMode(loadedCityMode);
        else setCityEntryMode('list');
      });
    }
  }, [id, isEdit, countries]);

  // For "create lead" screens, ensure `form.source` is a valid active option.
  useEffect(() => {
    if (isEdit) return;
    if (!sources.length) return;
    const activeSourceNames = sources.map(s => s.source_name).filter(Boolean);
    if (form.source && activeSourceNames.includes(form.source)) return;
    setForm(f => ({ ...f, source: activeSourceNames[0] || f.source }));
  }, [sources, isEdit, form.source]);

  const validate = () => {
    const errs = {};
    if (!form.student_name) errs.student_name = t('leadForm.nameRequired');
    if (!phoneE164) errs.contact_number = t('leadForm.contactRequired');
    else if (!isValidPhoneNumber(phoneE164)) errs.contact_number = t('leadForm.invalidPhone');
    if (!form.email) errs.email = t('leadForm.emailRequired');
    else if (!/\S+@\S+\.\S+/.test(form.email)) errs.email = t('leadForm.invalidEmail');
    if (!form.country) errs.country = t('leadForm.countryRequired');
    if (!form.state) errs.state = t('leadForm.stateRequired');
    if (!String(form.city || '').trim()) errs.city = t('leadForm.cityRequired');
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const phoneParts = splitPhoneForApi(phoneE164);
      const data = { ...form, contact_number: phoneParts.contact_number, phone_country_code: phoneParts.phone_country_code };
      if (data.course_id === '') data.course_id = null;
      if (data.intake_id === '') data.intake_id = null;
      if (canAssignOwner) {
        if (data.owner_id === '') data.owner_id = null;
      } else {
        data.owner_id = currentUser?.user_id || null;
      }
      if (data.agency_id === '') data.agency_id = null;
      if (canAssignCallerCloser) {
        if (data.closed_by === '') data.closed_by = null;
      } else {
        delete data.closed_by;
      }

      if (isEdit) {
        await b2bAgenciesAPI.update(id, data);
        toast.success(t('leadForm.updateSuccess'));
        navigate(`/b2b-agencies/${id}`);
      } else {
        const res = await b2bAgenciesAPI.create(data);
        const fromProspectId = searchParams.get('fromProspect');
        if (fromProspectId) {
          try {
            await prospectsAPI.convert(fromProspectId, { entity_type: 'agency', entity_id: res.data.agency_id });
          } catch {}
        }
        toast.success(t('leadForm.createSuccess'));
        navigate(`/b2b-agencies/${res.data.agency_id}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || t('leadForm.saveFailed'));
    }
    setLoading(false);
  };

  const handleChange = (field, value) => {
    setForm(f => ({ ...f, [field]: value }));
    if (errors[field]) setErrors(e => ({ ...e, [field]: '' }));

    if (field === 'lead_type') return;

    // Campus controls available courses for B2C/B2B-S
    if (field === 'campus_id') {
      const campusIdNum = Number(value || 0);
      if (campusIdNum > 0) {
        const allowedCourseIds = new Set(
          (courseCampusFees || [])
            .filter(m => Number(m.campus_id) === campusIdNum)
            .map(m => Number(m.course_id))
        );
        setForm(f => ({
          ...f,
          campus_id: value,
          course_id: f.course_id && allowedCourseIds.has(Number(f.course_id)) ? f.course_id : ''
        }));
      } else {
        setForm(f => ({ ...f, campus_id: value, course_id: '' }));
      }
    }
  };

  const handleCountrySelect = (countryCode) => {
    setCityEntryMode('list');
    const selected = countries.find(c => c.isoCode === countryCode);
    setForm(f => ({
      ...f,
      location_country_code: countryCode,
      country: selected?.name || '',
      location_state_code: '',
      state: '',
      city: ''
    }));
    if (errors.country) setErrors(e => ({ ...e, country: '' }));
    if (errors.state) setErrors(e => ({ ...e, state: '' }));
    if (errors.city) setErrors(e => ({ ...e, city: '' }));
  };

  const handleStateSelect = (stateCode) => {
    setCityEntryMode('list');
    const selected = states.find(s => s.isoCode === stateCode);
    setForm(f => ({
      ...f,
      location_state_code: stateCode,
      state: selected?.name || '',
      city: ''
    }));
    if (errors.state) setErrors(e => ({ ...e, state: '' }));
    if (errors.city) setErrors(e => ({ ...e, city: '' }));
  };

  const handleCitySelect = (cityName) => {
    setForm(f => ({ ...f, city: cityName }));
    if (errors.city) setErrors(e => ({ ...e, city: '' }));
  };

  const handlePhoneChange = (value) => {
    setPhoneE164(value || '');
    if (errors.contact_number) setErrors(e => ({ ...e, contact_number: '' }));
  };

  const splitPhoneForApi = (e164) => {
    if (!e164) return { phone_country_code: config.defaultPhoneCode, contact_number: '' };
    try {
      const parsed = parsePhoneNumber(e164);
      if (parsed?.countryCallingCode && parsed?.nationalNumber) {
        return { phone_country_code: `+${parsed.countryCallingCode}`, contact_number: parsed.nationalNumber };
      }
    } catch {}
    return { phone_country_code: config.defaultPhoneCode, contact_number: e164.replace(/^\+/, '') };
  };

  const isB2B = true;
  const currentRole = currentUser?.role;
  const canAssignOwner = ['super_admin', 'admin', 'manager'].includes(currentRole);
  const canAssignCallerCloser = ['super_admin', 'admin'].includes(currentRole);
  const isSalesExecutive = currentRole === 'sales_executive';
  const filteredCourses = useMemo(() => {
    if (isB2B) return courses;
    const campusIdNum = Number(form.campus_id || 0);
    if (!campusIdNum) return courses;
    const allowedCourseIds = new Set(
      (courseCampusFees || [])
        .filter(m => Number(m.campus_id) === campusIdNum)
        .map(m => Number(m.course_id))
    );
    return (courses || []).filter(c => allowedCourseIds.has(Number(c.course_id)));
  }, [isB2B, form.campus_id, courses, courseCampusFees]);
  const phoneValue = phoneE164 || undefined;

  // Use only configured Lead Statuses from Settings for B2B.
  const statusOptions = (leadStatuses || [])
    .filter(s => s.lead_group === 'B2B' && s.status === 'active')
    .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
    .map(s => s.status_name);
  if (form.status && !statusOptions.includes(form.status)) {
    statusOptions.unshift(form.status);
  }

  return (
    <div>
      <div className="page-header">
        <h1>{isEdit ? t('leadForm.editTitle') : t('leadForm.addTitle')}</h1>
      </div>

      {!isEdit && isB2B && (
        <div className="card" style={{ marginBottom: 16, padding: 16, background: 'var(--info-light)', border: '1px solid var(--info)' }}>
          <strong>{t('leadForm.infoTitle')}</strong> {t('leadForm.infoDesc')}
          <br />
          <small style={{ color: 'var(--text-secondary)' }}>
            <strong>{t('leadForm.noteTitle')}</strong> {t('leadForm.noteDesc')}
          </small>
        </div>
      )}

      <div className="card">
        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            {isEdit && (
              <div className="form-group">
                <label>{t('leadForm.refNumber')}</label>
                <input type="text" disabled value={form.ref_number || ''} style={{ background: '#f5f5f5' }} />
              </div>
            )}

            <div className="form-group">
              <label>{t('leadForm.agencyName')} <span className="required">*</span></label>
              <input type="text" placeholder={t('leadForm.agencyNamePlaceholder')}
                value={form.student_name} onChange={(e) => handleChange('student_name', e.target.value)}
                className={errors.student_name ? 'error' : ''}
              />
              {errors.student_name && <div className="error-text">{errors.student_name}</div>}
            </div>

            <div className="form-group">
              <label>{t('leadForm.contactNumber')} <span className="required">*</span></label>
              <PhoneInput
                international
                defaultCountry={config.flagCode}
                value={phoneValue}
                onChange={handlePhoneChange}
                placeholder={t('leadForm.phonePlaceholder')}
                className={errors.contact_number ? 'error' : ''}
              />
              {errors.contact_number && <div className="error-text">{errors.contact_number}</div>}
            </div>

            <div className="form-group">
              <label>{t('leadForm.email')} <span className="required">*</span></label>
              <input type="email" placeholder={t('leadForm.emailPlaceholder')} value={form.email} onChange={(e) => handleChange('email', e.target.value)} className={errors.email ? 'error' : ''} />
              {errors.email && <div className="error-text">{errors.email}</div>}
            </div>

            <div className="form-group">
              <label>{t('leadForm.source')}</label>
              <select value={form.source} onChange={(e) => handleChange('source', e.target.value)}>
                {sourceOptions.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            {isEdit && (
              <div className="form-group">
                <label>{t('leadForm.status')}</label>
                <select value={form.status} onChange={(e) => handleChange('status', e.target.value)}>
                  {statusOptions.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            )}

            {canAssignOwner && (
              <div className="form-group">
                <label>{t('leadForm.owner')}</label>
                <select value={form.owner_id} onChange={(e) => handleChange('owner_id', e.target.value)}>
                  <option value="">{t('leadForm.defaultOwner')}</option>
                  {salesTeam.map(u => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
                </select>
              </div>
            )}
            {isSalesExecutive && (
              <div className="form-group">
                <label>{t('leadForm.owner')}</label>
                <input type="text" value={currentUser?.name || 'Me'} disabled style={{ background: '#f5f5f5' }} />
              </div>
            )}

            {isEdit && canAssignCallerCloser && (
              <div className="form-group">
                <label>{t('leadDetail.closedBy')}</label>
                <select value={form.closed_by} onChange={(e) => handleChange('closed_by', e.target.value)}>
                  <option value="">{t('common.none')}</option>
                  {salesTeam.map(u => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
                </select>
              </div>
            )}

            <>
                <div className="form-group">
                  <label>{t('leadForm.country')} <span className="required">*</span></label>
                  <select
                    value={form.location_country_code || ''}
                    onChange={(e) => handleCountrySelect(e.target.value)}
                    className={errors.country ? 'error' : ''}
                  >
                    <option value="">{t('common.selectCountry')}</option>
                    {countries.map(c => (
                      <option key={c.isoCode} value={c.isoCode}>{c.name}</option>
                    ))}
                  </select>
                  {errors.country && <div className="error-text">{errors.country}</div>}
                </div>

                <div className="form-group">
                  <label>{t('leadForm.state')} <span className="required">*</span></label>
                  <select
                    value={form.location_state_code || ''}
                    onChange={(e) => handleStateSelect(e.target.value)}
                    className={errors.state ? 'error' : ''}
                    disabled={!form.location_country_code || states.length === 0}
                  >
                    <option value="">{states.length ? t('common.selectState') : t('common.selectCountryFirst')}</option>
                    {states.map(s => (
                      <option key={s.isoCode} value={s.isoCode}>{s.name}</option>
                    ))}
                  </select>
                  {errors.state && <div className="error-text">{errors.state}</div>}
                </div>

                <div className="form-group">
                  <label>{t('leadForm.city')} <span className="required">*</span></label>
                  {!form.location_state_code ? (
                    <input type="text" disabled value="" placeholder={t('common.selectStateFirst')} className={errors.city ? 'error' : ''} />
                  ) : cities.length > 0 ? (
                    <>
                      <select
                        value={cityEntryMode === 'other' ? CITY_SELECT_OTHER_VALUE : (form.city || '')}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === CITY_SELECT_OTHER_VALUE) {
                            setCityEntryMode('other');
                            setForm((f) => ({ ...f, city: '' }));
                          } else {
                            setCityEntryMode('list');
                            handleCitySelect(v);
                          }
                        }}
                        className={errors.city ? 'error' : ''}
                      >
                        <option value="">{t('common.selectCity')}</option>
                        {cities.map((c) => (
                          <option key={c.name} value={c.name}>{c.name}</option>
                        ))}
                        <option value={CITY_SELECT_OTHER_VALUE}>{t('common.otherEnterCity')}</option>
                      </select>
                      {cityEntryMode === 'other' && (
                        <input
                          type="text"
                          value={form.city || ''}
                          onChange={(e) => handleCitySelect(e.target.value)}
                          className={errors.city ? 'error' : ''}
                          placeholder={t('common.typeCityName')}
                          style={{ marginTop: 8 }}
                        />
                      )}
                    </>
                  ) : (
                    <input
                      type="text"
                      value={form.city || ''}
                      onChange={(e) => handleCitySelect(e.target.value)}
                      className={errors.city ? 'error' : ''}
                      placeholder={t('common.enterCity')}
                    />
                  )}
                  {errors.city && <div className="error-text">{errors.city}</div>}
                </div>

                <div className="form-group">
                  <label>{t('leadForm.contactPerson')}</label>
                  <input
                    type="text"
                    placeholder={t('leadForm.contactPersonPlaceholder')}
                    value={form.decision_maker}
                    onChange={(e) => handleChange('decision_maker', e.target.value)}
                  />
                </div>
            </>

            <div className="form-group full-width">
              <label>{t('leadForm.remarks')}</label>
              <textarea placeholder={t('leadForm.remarksPlaceholder')} value={form.remarks} onChange={(e) => handleChange('remarks', e.target.value)} maxLength={1000} />
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-outline" onClick={() => navigate('/b2b-agencies')}>{t('common.cancel')}</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? t('leadForm.savingBtn') : (isEdit ? t('leadForm.updateBtn') : t('leadForm.saveBtn'))}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LeadForm;
